import { jsx as _jsx } from "react/jsx-runtime";
import { InputControl, TextField } from '@/components';
import { useDebounce, useResolveValue } from '@/hooks';
import { ValuesType } from '@/types';
import { REGEX, decimalSeparator, errorDebounceMillis, getInputBlockAnswer } from '@/utils';
import { useRef } from 'react';
import { BlockLayout } from '../fragments/BlockLayout';
export const FreeDecimalField = ({ block: { answer, id, input_mode: { max_decimal_places, type_validation_error_message }, key, layout, placeholder_text, required }, defaultValue = null, errors = [], onChange }) => {
    const inputRef = useRef();
    const [resolver] = useResolveValue();
    const debouncedValue = useDebounce(defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.raw_value, errorDebounceMillis);
    const debounceError = useDebounce(errors, errorDebounceMillis);
    const isInvalid = (debounceError === null || debounceError === void 0 ? void 0 : debounceError.length) > 0 && (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.raw_value) !== null && debouncedValue !== null;
    const resolved = {
        label: resolver.resolveString(answer.label),
        placeholder: resolver.resolveString(placeholder_text),
        required: resolver.resolveBoolean(required),
        type_validation_error_message: resolver.resolveString(type_validation_error_message)
    };
    function removePrecedingZeros(decimal) {
        const parts = decimal.split(decimalSeparator);
        // Remove preceding zeros except for the first part
        parts[0] = parts[0].replace(/^0+(?!$)/, '');
        // Join the parts back together with the separator
        return parts.join(decimalSeparator);
    }
    const handleChange = (e) => {
        // an answered value comes back formatted for the locale (e.g. `123,456.12`), so the group
        // separators have to be dropped before the value can be treated as a decimal again
        const value = removePrecedingZeros(e.target.value.replace(REGEX.NON_DECIMAL_CHARACTERS, ''));
        const parts = value.split(decimalSeparator);
        const hasFractionalPart = parts.length > 1;
        const integerPart = parts[0];
        let fractionalPart = parts[1];
        if (hasFractionalPart) {
            if (fractionalPart.length > max_decimal_places) {
                fractionalPart = fractionalPart.slice(0, max_decimal_places);
            }
        }
        const newValue = value === ''
            ? null
            : integerPart + (hasFractionalPart ? decimalSeparator + fractionalPart : '');
        const answerValue = Object.assign(Object.assign({}, answer), { label: resolved.label, value: newValue });
        inputRef.current.value = newValue;
        const newAnswer = getInputBlockAnswer(ValuesType.DECIMAL, answerValue, key);
        onChange(newAnswer);
    };
    const handlePaste = (e) => {
        e.preventDefault();
    };
    const handleKeyDown = (e) => {
        if (![
            'ArrowLeft',
            'ArrowRight',
            'Backspace',
            'Delete',
            'Tab',
            '0',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            decimalSeparator
        ].includes(e.key))
            e.preventDefault();
    };
    return (_jsx(BlockLayout, Object.assign({ layout: layout }, { children: _jsx(InputControl, Object.assign({ "data-test": id, isInvalid: isInvalid, isRequired: resolved.required, label: resolved.label }, { children: _jsx(TextField, { "data-test": answer.id, defaultValue: (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.raw_value) || '', error: errors[0] || '', inputMode: 'decimal', onChange: handleChange, onKeyDown: handleKeyDown, onPaste: handlePaste, placeholder: resolved.placeholder, ref: inputRef, type: 'text' }) })) })));
};
